import { useMemo } from 'react';

import type { ReplyData } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useCommentsPanelState } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';

import {
	computeVisibleReplies,
	type ComputeVisibleRepliesProps,
} from '../helpers/repliesHelpers.ts';

export type UseVisibleRepliesProps = Omit<ComputeVisibleRepliesProps, 'currentView'>;

// Returns the visible replies for a comment thread. The value is derived during
// render rather than stored as state + reconciled in an effect; this keeps the
// first paint flicker-free without `useLayoutEffect`, and makes the value
// available during SSR so server-rendered comment threads include their
// replies.
export const useVisibleReplies = ({
	allReplies,
	isParentCommentOpen,
	currentReplyView,
	latestReplies,
	parentCommentId,
	allowNestedReplies = false,
}: UseVisibleRepliesProps): ReplyData[] => {
	const { currentView } = useCommentsPanelState();

	return useMemo(
		() =>
			computeVisibleReplies({
				parentCommentId,
				currentReplyView,
				allReplies,
				isParentCommentOpen,
				latestReplies,
				currentView,
				allowNestedReplies,
			}),
		[
			allReplies,
			currentReplyView,
			isParentCommentOpen,
			latestReplies,
			parentCommentId,
			currentView,
			allowNestedReplies,
		],
	);
};
